import React, { useEffect, useState, useCallback } from "react";
import { Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { creditsApi } from "../lib/api";
import BuyCreditsModal from "./BuyCreditsModal";

const fmt = (n) => Number(n || 0).toLocaleString();

export default function CreditsPill() {
  const { user } = useAuth();
  const [bal, setBal] = useState(null);
  const [open, setOpen] = useState(false);

  const isStaff = user && user !== false && user.role !== "super_admin" && user.companyId;
  const isAdmin = isStaff && user.role === "admin";

  const load = useCallback(() => {
    if (!isStaff) return;
    creditsApi.balance().then(setBal).catch(() => {});
  }, [isStaff]);

  useEffect(() => {
    if (!isStaff) return;
    load();
    const t = setInterval(load, 45000);
    const onFocus = () => load();
    const onUpdate = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("credits-updated", onUpdate);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("credits-updated", onUpdate);
    };
  }, [isStaff, load]);

  if (!isStaff || !bal || !bal.enabled) return null;

  const low = bal.low;
  const empty = bal.empty;
  const tone = empty
    ? "bg-red-500 text-white border-red-600/40"
    : low
    ? "bg-amber-400 text-amber-950 border-amber-500/40 animate-[pulse_2.5s_ease-in-out_infinite]"
    : "bg-amber-400 text-amber-950 border-amber-500/30";

  return (
    <>
      <button
        type="button"
        onClick={() => isAdmin && setOpen(true)}
        disabled={!isAdmin}
        data-testid="credits-pill"
        title={isAdmin ? "Buy AI credits" : `${fmt(bal.totalDisplay)} AI credits remaining`}
        className={`hidden md:inline-flex items-center gap-1.5 h-8 pl-1.5 pr-3 rounded-full border text-[12.5px] font-bold transition-transform ${tone} ${
          isAdmin ? "hover:scale-[1.03] cursor-pointer" : "cursor-default"
        }`}
      >
        <span className={`h-5 w-5 rounded-full flex items-center justify-center ${empty ? "bg-white/25" : "bg-amber-950"}`}>
          <Sparkles className={`h-3 w-3 ${empty ? "text-white" : "text-amber-300"}`} />
        </span>
        <span data-testid="credits-pill-balance" className="tabular-nums">{fmt(bal.totalDisplay)}</span>
        <span className="opacity-80">Credits</span>
      </button>

      {isAdmin && <BuyCreditsModal open={open} onClose={() => setOpen(false)} />}
    </>
  );
}
